import type {
  MessageNode,
  EditableGraph,
  Id,
  FileUploadNode,
} from 'src/types/chatbot';
import type {EntityType} from 'src/types/ats-entities';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {useEditorState} from './reducer';
import {findAncestorNodeVariables} from '../utils';
import {validateNode} from './validation';

import EditorContainer from './container';
import {MessageDraftEditor} from 'src/components/conversation-builder/draft-editor';
import {PadSection, useFirstSmsMessage} from './common';
import {type RawDraftContentState} from 'draft-js';


export default function MessageEditor({
  graph,
  defaultNode,
  entityType,
  hasSubmitted,
  edgeId,
  ...props
}: {
  graph: EditableGraph;
  entityType: EntityType;
  onClose: () => unknown;
  onSubmit: (arg1: MessageNode | FileUploadNode) => unknown;
  defaultNode: MessageNode | FileUploadNode;
  actionName: 'add' | 'edit';
  hasSubmitted: boolean;
  edgeId?: Id;
  readOnly: unknown;
}) {
  const {t} = useLabelTranslation();
  const isFirstSmsMessage = useFirstSmsMessage(graph, defaultNode);

  const [state, dispatch] = useEditorState({defaultNode});

  const update = (
    payload:
      | {
          text_draft_json: RawDraftContentState;
        }
      | Partial<MessageNode | FileUploadNode>,
  ) => dispatch({type: 'update', payload});
  const {node} = state;

  const error = hasSubmitted ? validateNode(node, graph) : null;

  const nodeVariables = findAncestorNodeVariables(graph, defaultNode, edgeId);

  return (
    <EditorContainer onUpdate={update} state={state} error={error} {...props}>
      <PadSection>
        <MessageDraftEditor
          label={t('Message', 'Message')}
          placeholder={`${t('Enter_a_question', 'Enter a question')}...`}
          defaultValue={node.text_draft_json}
          onChange={(text_draft_json) => update({text_draft_json})}
          entityType={entityType}
          isFirstSmsMessage={isFirstSmsMessage}
          error={error?.text_draft_json}
          readOnly={Boolean(props.readOnly)}
          useCase={graph.use_case}
          nodeVariables={nodeVariables}
        />
      </PadSection>
    </EditorContainer>
  );
}
